<template>
  <el-dialog
    width="55%"
    title="Bulk Approve for On-Air"
    destroy-on-close
    :visible.sync="internalVisible"
    @close="handleClose"
  >
    <div class="bulk-summary">
      <div class="summary-stat">
        <span class="summary-number">{{ notes.length }}</span>
        <span class="summary-label">Voice notes selected</span>
      </div>
      <div class="summary-stat">
        <span class="summary-number">{{ alreadyApproved.length }}</span>
        <span class="summary-label">Already on-air</span>
      </div>
      <div class="summary-stat">
        <span class="summary-number">{{ toApprove.length }}</span>
        <span class="summary-label">Will be approved</span>
      </div>
    </div>

    <el-alert
      v-if="alreadyApproved.length > 0"
      type="warning"
      :closable="false"
      show-icon
      style="margin-bottom: 16px"
    >
      <span slot="title">
        {{ alreadyApproved.length }} voice note{{ alreadyApproved.length !== 1 ? 's are' : ' is' }}
        already on-air and will be skipped.
      </span>
    </el-alert>

    <div class="notes-list">
      <div
        v-for="note in notes"
        :key="note.id"
        class="note-item"
        :class="{ 'note-item--approved': note.approved }"
      >
        <div class="note-item-left">
          <i class="el-icon-user"></i>
          <span class="note-sender">{{ note.sender }}</span>
          <span class="note-time">{{ formatDate(note.timestamp) }}</span>
        </div>
        <div class="note-item-right">
          <el-tag v-if="note.approved" size="mini" type="success" effect="dark">
            <i class="fa fa-podcast"></i> Already On Air
          </el-tag>
          <el-tag v-else-if="note.flagged" size="mini" type="danger" effect="plain">
            <i class="fa fa-flag"></i> Flagged
          </el-tag>
          <el-tag v-else size="mini" type="info" effect="plain">
            <i class="el-icon-check"></i> Ready to Approve
          </el-tag>
        </div>
      </div>
    </div>

    <el-divider />

    <el-form label-position="top" size="small">
      <el-form-item label="Approving As">
        <el-input :value="approvedBy" disabled>
          <i slot="prefix" class="el-icon-user el-input__icon"></i>
        </el-input>
      </el-form-item>

      <el-form-item label="Send On-Air To">
        <el-select
          v-model="computedOnAirUser"
          filterable
          placeholder="Select on-air user"
          style="width: 100%"
        >
          <el-option
            v-for="user in companyUsers"
            :key="user.id"
            :label="user.display_name"
            :value="user.id"
          >
            <span>{{ user.display_name }}</span>
            <span v-if="user.id === me.id" style="float: right; color: #67c23a; font-size: 12px">
              <i class="el-icon-user"></i> You
            </span>
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="Date of Approval">
        <el-input :value="approvalDate" disabled>
          <i slot="prefix" class="el-icon-date el-input__icon"></i>
        </el-input>
      </el-form-item>

      <el-form-item label="Batch Comment (applies to all)">
        <el-input
          type="textarea"
          v-model="comments"
          placeholder="Add a reason or comment for this batch approval..."
          :rows="3"
          maxlength="300"
          show-word-limit
        />
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleClose">Cancel</el-button>
      <el-button
        size="small"
        type="primary"
        :loading="loading"
        :disabled="toApprove.length === 0"
        @click="handleApprove"
      >
        Approve {{ toApprove.length }} Voice Note{{ toApprove.length !== 1 ? 's' : '' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';

export default {
  name: 'BulkApproveModal',
  props: {
    visible: { type: Boolean, required: true },
    notes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      comments: '',
      selectedOnAirUser: null,
    };
  },
  mounted() {
    this.$store.dispatch('user/getCompanyUsers');
  },
  computed: {
    ...mapState('user', ['companyUsers', 'me']),
    internalVisible: {
      get() {
        return this.visible;
      },
      set(v) {
        this.$emit('update:visible', v);
      },
    },
    computedOnAirUser: {
      get() {
        return this.selectedOnAirUser || null;
      },
      set(onAirUser) {
        this.selectedOnAirUser = onAirUser;
      },
    },
    approvedBy() {
      return this.me ? this.me.display_name : '';
    },
    approvalDate() {
      return moment().format('DD MMM YYYY, HH:mm');
    },
    alreadyApproved() {
      return this.notes.filter(n => n.approved);
    },
    toApprove() {
      return this.notes.filter(n => !n.approved);
    },
  },
  methods: {
    formatDate(timestamp) {
      return moment(timestamp).format('DD MMM YYYY, HH:mm');
    },
    handleApprove() {
      const userId = this.computedOnAirUser;
      if (!userId) {
        this.$notify.warning('Please select an on-air user');
        return;
      }

      this.$emit('bulk-approve', {
        notes: this.toApprove,
        approvedBy: this.me.display_name,
        approvedById: this.me.id,
        approveStatus: true,
        reason: this.comments,
      });

      this.toApprove.forEach(note => {
        this.$store.dispatch('interaction/sendToOnAir', {
          interactionId: note.id,
          userId,
        });

        if (this.comments && this.comments.trim()) {
          this.$store.dispatch('interaction/sendComment', {
            interactionId: note.id,
            message: this.comments,
          });
        }
      });
    },
    handleClose() {
      this.comments = '';
      this.selectedOnAirUser = null;
      this.internalVisible = false;
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.bulk-summary {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 6px;
}

.summary-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding: 8px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e9e9e9;
}

.summary-number {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
}

.summary-label {
  font-size: 11px;
  color: #909399;
  margin-top: 4px;
  text-align: center;
}

.notes-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e9e9e9;
  border-radius: 6px;
  margin-bottom: 16px;
}

.note-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}

.note-item:last-child {
  border-bottom: none;
}

.note-item--approved {
  background: #f9f9f9;
  opacity: 0.7;
}

.note-item-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #303133;
}

.note-sender {
  font-weight: 500;
}

.note-time {
  font-size: 11px;
  color: #909399;
}

.note-item-right {
  flex-shrink: 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.notes-list::-webkit-scrollbar {
  width: 4px;
}

.notes-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}
</style>


// WEBPACK FOOTER //
// BulkApproveModal.vue?23d7e84c